/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useActionable } from '../hooks/UNSAFE_useActionable';
import { cssProps } from '@oracle/oraclejet-cssprops';
import { useCallback } from 'preact/hooks';
import { classNames } from '../utils/UNSAFE_classNames';
import { useTabbableMode } from '../hooks/UNSAFE_useTabbableMode';

const cursorandFocusStyle = cssProps`${{
  cursor: 'var( --oj-c-PRIVATE-DO-NOT-USE-core-cursor-clickable)',
  '&:focus-visible': {
    outlineColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-focus-border-color)',
    outlineStyle: 'dotted',
    outlineWidth: '1px',
    outlineOffset: '1px'
  }
}}`;

const styles = {
  base: cssProps`${{
    backgroundColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-chip-bg-color)',
    color: 'var(--oj-c-PRIVATE-DO-NOT-USE-chip-text-color)',
    display: 'inline-block',
    paddingTop: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-2x)',
    paddingRight: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-2x)',
    paddingBottom: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-2x)',
    paddingLeft: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-2x)',
    boxSizing: 'border-box',
    borderTopLeftRadius: 'var(--oj-c-PRIVATE-DO-NOT-USE-chip-border-radius)',
    borderTopRightRadius: 'var(--oj-c-PRIVATE-DO-NOT-USE-chip-border-radius)',
    borderBottomRightRadius: 'var(--oj-c-PRIVATE-DO-NOT-USE-chip-border-radius)',
    borderBottomLeftRadius: 'var(--oj-c-PRIVATE-DO-NOT-USE-chip-border-radius)',
    height: '2rem',
    minWidth: '2rem',
    lineHeight: 'var(--oj-c-PRIVATE-DO-NOT-USE-chip-line-height)',
    fontSize: 'var(--oj-c-PRIVATE-DO-NOT-USE-chip-font-size)',
    fontWeight: 'var(--oj-c-PRIVATE-DO-NOT-USE-chip-font-weight)',
    overflowX: 'hidden',
    overflowY: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    textAlign: 'center'
  }}`,
  disabled: cssProps`${{
    '--oj-c-PRIVATE-DO-NOT-USE-chip-bg-color':
      'var(--oj-c-PRIVATE-DO-NOT-USE-core-color-disabled-1)',
    '--oj-c-PRIVATE-DO-NOT-USE-chip-text-color':
      'var(--oj-c-PRIVATE-DO-NOT-USE-core-text-color-disabled)'
  }}`,
  info: cssProps`${{
    '--oj-c-PRIVATE-DO-NOT-USE-chip-bg-color': 'var(--oj-c-PRIVATE-DO-NOT-USE-core-info-1)',
    '--oj-c-PRIVATE-DO-NOT-USE-chip-text-color': 'var(--oj-c-PRIVATE-DO-NOT-USE-core-info-contrast)'
  }}`,
  infoSubtle: cssProps`${{
    '--oj-c-PRIVATE-DO-NOT-USE-chip-bg-color':
      'var(--oj-c-PRIVATE-DO-NOT-USE-core-info-secondary-1)',
    '--oj-c-PRIVATE-DO-NOT-USE-chip-text-color':
      'var(--oj-c-PRIVATE-DO-NOT-USE-core-info-secondary-contrast)'
  }}`,
  infoSubtleLighter: cssProps`${{
    '--oj-c-PRIVATE-DO-NOT-USE-chip-bg-color':
      'var(--oj-c-PRIVATE-DO-NOT-USE-core-info-secondary-3)',
    '--oj-c-PRIVATE-DO-NOT-USE-chip-text-color':
      'var(--oj-c-PRIVATE-DO-NOT-USE-core-info-secondary-contrast)'
  }}`,

  childrenMargins: cssProps`${{
    /* @ts-ignore */
    '& > *': {
      marginTop: '0 !important',
      marginBottom: '0 !important',
      marginLeft: '0 !important',
      marginRight: '0 !important'
    }
  }}`
};

type SelectedChangeDetail = { previousValue?: boolean; value?: boolean };

type Props = {
  /**
   * Specifies if the chip component is selected.
   */
  isSelected?: boolean;
  /**
   * Disables the component.
   */
  isDisabled?: boolean;
  /**
   * Specifies some screen reader text.
   */
  accessibleLabel?: string;
  children: string;
  /**
   * Triggered when a chip is clickable, whether by keyboard, mouse, or touch events.
   */
  onToggle?: (detail: SelectedChangeDetail) => void;
  //
};

export function Chip({ isSelected, isDisabled, accessibleLabel, children, onToggle }: Props) {
  const handleAction = useCallback(() => {
    onToggle?.({ previousValue: isSelected, value: !isSelected });
  }, [isSelected, onToggle]);

  const { actionableProps, isHover, isActive } = useActionable(handleAction);

  const variant = isDisabled
    ? 'disabled'
    : isSelected || isActive
    ? 'info'
    : isHover
    ? 'infoSubtle'
    : 'infoSubtleLighter';

  const classes = classNames([
    styles.base,
    isDisabled ? styles.disabled : `${styles.base} ${styles[variant]} ${cursorandFocusStyle}`,
    styles.childrenMargins
  ]);
  const { isTabbable } = useTabbableMode();

  return isDisabled ? (
    <div class={classes}>{children}</div>
  ) : (
    <div
      {...actionableProps}
      class={classes}
      tabIndex={isTabbable ? 0 : -1}
      aria-label={accessibleLabel}
      role="switch"
      aria-checked={isSelected ? 'true' : 'false'}>
      {children}
    </div>
  );
}
